import { z } from 'zod';
import { INTERVENTION_STATUSES, MEDIA_PHASES, PAYMENT_METHODS, PAYMENT_STATUSES } from '../enums';
import { emailSchema, idSchema, moneySchema, phoneSchema } from './common';
import { addressSchema, bicycleSchema, createAddressSchema, createBicycleSchema } from './users';
// =============================================================================
// Reservation (RG-01 a RG-04, RG-07)
// =============================================================================
/**
 * Coordonnees du client non authentifie.
 * RG-04 : la reservation precede la creation du compte. Un utilisateur GUEST est cree
 * a la volee, puis converti lorsque le client finalise son inscription.
 */
export const guestContactSchema = z.object({
    email: emailSchema,
    firstName: z.string().trim().min(2, 'Prenom requis.').max(100),
    lastName: z.string().trim().min(2, 'Nom requis.').max(100),
    phone: phoneSchema,
});
export const createBookingSchema = z
    .object({
    /** Absent si le client est authentifie ; requis sinon. */
    guest: guestContactSchema.optional(),
    /** Adresse existante du client connecte, ou nouvelle adresse geocodee. */
    addressId: idSchema.optional(),
    newAddress: createAddressSchema.optional(),
    /** Cycle existant du client connecte, ou nouveau cycle a enregistrer. */
    bicycleId: idSchema.optional(),
    newBicycle: createBicycleSchema.optional(),
    packageId: idSchema,
    /**
     * Creneaux consecutifs a reserver, tels que renvoyes par le moteur de
     * disponibilite. L'API revalide integralement leur contiguite et leur
     * disponibilite : ces identifiants viennent du client, ils ne sont pas dignes
     * de confiance.
     */
    slotIds: z.array(idSchema).min(1, 'Selectionnez un creneau.').max(16),
    products: z
        .array(z.object({
        productId: idSchema,
        quantity: z.number().int().min(1).max(99),
    }))
        .max(30)
        .default([]),
    /** Cles des photos deja televersees sur MinIO pendant le tunnel (phase BEFORE). */
    mediaKeys: z.array(z.string().max(512)).max(10).default([]),
    clientNotes: z.string().trim().max(2000).optional().nullable(),
    acceptTerms: z.literal(true, {
        errorMap: () => ({ message: 'Vous devez accepter les conditions generales de vente.' }),
    }),
})
    .refine((data) => !!data.addressId !== !!data.newAddress, {
    message: 'Fournissez soit une adresse existante, soit une nouvelle adresse.',
    path: ['addressId'],
})
    .refine((data) => !!data.bicycleId !== !!data.newBicycle, {
    message: 'Fournissez soit un cycle existant, soit un nouveau cycle.',
    path: ['bicycleId'],
});
export const bookingResultSchema = z.object({
    interventionId: idSchema,
    reference: z.string(),
    status: z.enum(INTERVENTION_STATUSES),
    startsAt: z.string(),
    endsAt: z.string(),
    totalAmount: z.number(),
    /**
     * Present uniquement pour une reservation faite sans compte : ce jeton permet
     * de rattacher la reservation au compte cree juste apres (RG-04).
     */
    guestClaimToken: z.string().uuid().optional(),
});
/**
 * Reservation creee par un administrateur pour le compte d'un client (cas du client
 * qui telephone). Le forcage permet de passer outre l'indisponibilite d'un creneau ;
 * il exige un motif et est trace dans le journal d'audit.
 */
export const adminBookingSchema = z
    .object({
    clientId: idSchema,
    addressId: idSchema,
    bicycleId: idSchema,
    packageId: idSchema,
    slotIds: z.array(idSchema).min(1).max(16),
    products: z
        .array(z.object({ productId: idSchema, quantity: z.number().int().min(1).max(99) }))
        .max(30)
        .default([]),
    clientNotes: z.string().trim().max(2000).optional().nullable(),
    force: z.boolean().default(false),
    forceReason: z.string().trim().max(255).optional().nullable(),
})
    .refine((data) => !data.force || (data.forceReason?.length ?? 0) >= 5, {
    message: 'Un motif est obligatoire pour forcer un creneau indisponible.',
    path: ['forceReason'],
});
// =============================================================================
// Intervention
// =============================================================================
export const interventionProductSchema = z.object({
    id: idSchema,
    productId: idSchema,
    name: z.string(),
    quantity: z.number().int(),
    /** Prix unitaire fige a la reservation (RG-10). */
    unitPriceSnapshot: z.number(),
});
export const interventionMediaSchema = z.object({
    id: idSchema,
    storageKey: z.string(),
    url: z.string(),
    phase: z.enum(MEDIA_PHASES),
    uploadedById: idSchema,
    createdAt: z.string(),
});
export const interventionCommentSchema = z.object({
    id: idSchema,
    content: z.string(),
    authorId: idSchema,
    authorName: z.string(),
    authorRole: z.string(),
    createdAt: z.string(),
});
export const paymentSchema = z.object({
    id: idSchema,
    amount: z.number(),
    method: z.enum(PAYMENT_METHODS),
    status: z.enum(PAYMENT_STATUSES),
    paidAt: z.string().nullable(),
    collectedById: idSchema.nullable(),
});
export const interventionSchema = z.object({
    id: idSchema,
    /** Reference lisible communiquee au client : HCH-2026-000123. */
    reference: z.string(),
    status: z.enum(INTERVENTION_STATUSES),
    startsAt: z.string(),
    endsAt: z.string(),
    packageId: idSchema,
    packageName: z.string(),
    packagePriceSnapshot: z.number(),
    packageDurationMin: z.number().int(),
    totalAmount: z.number(),
    clientId: idSchema,
    client: z
        .object({
        id: idSchema,
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
        phone: z.string().nullable(),
        isGuest: z.boolean(),
    })
        .optional(),
    technicianId: idSchema,
    technician: z.object({ id: idSchema, firstName: z.string(), lastName: z.string() }).optional(),
    address: addressSchema.optional(),
    bicycle: bicycleSchema.partial().optional(),
    zoneId: idSchema,
    clientNotes: z.string().nullable(),
    techNotes: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    cancellationReason: z.string().nullable(),
    cancelledById: idSchema.nullable(),
    completedAt: z.string().nullable(),
    products: z.array(interventionProductSchema).optional(),
    media: z.array(interventionMediaSchema).optional(),
    comments: z.array(interventionCommentSchema).optional(),
    payment: paymentSchema.nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const interventionQuerySchema = z.object({
    status: z.enum(INTERVENTION_STATUSES).optional(),
    technicianId: idSchema.optional(),
    clientId: idSchema.optional(),
    zoneId: idSchema.optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    /** Raccourcis utilises par l'espace technicien. */
    scope: z.enum(['today', 'upcoming', 'past', 'all']).default('all'),
});
// --- Actions sur une intervention --------------------------------------------
export const changeInterventionStatusSchema = z.object({
    status: z.enum(INTERVENTION_STATUSES),
    reason: z.string().trim().max(500).optional().nullable(),
});
export const cancelInterventionSchema = z.object({
    reason: z.string().trim().min(3, 'Motif requis.').max(500),
});
export const updateInterventionSchema = z.object({
    techNotes: z.string().trim().max(5000).nullable().optional(),
    clientNotes: z.string().trim().max(2000).nullable().optional(),
    /** Le technicien peut ajouter des produits constates sur place. */
    products: z
        .array(z.object({ productId: idSchema, quantity: z.number().int().min(1).max(99) }))
        .max(30)
        .optional(),
});
export const createCommentSchema = z.object({
    content: z.string().trim().min(1, 'Commentaire vide.').max(2000),
});
export const registerPaymentSchema = z.object({
    method: z.enum(PAYMENT_METHODS),
    amount: moneySchema,
});
// --- Medias -------------------------------------------------------------------
/** Demande d'URL pre-signee : le fichier ne transite jamais par l'API. */
export const presignUploadSchema = z.object({
    fileName: z.string().trim().min(1).max(255),
    contentType: z.enum(['image/jpeg', 'image/png', 'image/webp'], {
        errorMap: () => ({ message: 'Formats acceptes : JPEG, PNG, WebP.' }),
    }),
    sizeBytes: z
        .number()
        .int()
        .positive()
        .max(10 * 1024 * 1024, 'Fichier trop volumineux (10 Mo max).'),
    phase: z.enum(MEDIA_PHASES),
});
export const presignUploadResultSchema = z.object({
    uploadUrl: z.string().url(),
    storageKey: z.string(),
    expiresIn: z.number().int(),
});
export const attachMediaSchema = z.object({
    storageKey: z.string().max(512),
    phase: z.enum(MEDIA_PHASES),
});
//# sourceMappingURL=interventions.js.map